/**
 * This file is part of Discorch.
 * Copyright (C) 2025 Discorch Contributors.
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published
 * by the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 */

import { cn } from "@/lib/utils";
import { GitBranch } from "lucide-react";
import React from "react";

function ActionButton({
  href,
  icon,
  children,
}: {
  href: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        "inline-flex w-full items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-semibold transition-colors duration-300 ease-in-out focus:ring-2 focus:outline-hidden sm:w-auto md:py-1.5",
        "bg-[#2b3137] text-white hover:bg-[#24292e] focus:ring-gray-500 active:bg-[#1b1f23]",
      )}
    >
      {icon}
      <span className="whitespace-nowrap">{children}</span>
    </a>
  );
}

export function Footer() {
  return (
    <footer className="mx-auto mt-12 w-full px-4 pb-8 md:max-w-2xl">
      <div className="bg-card rounded-lg border p-4 shadow-xs md:p-6">
        <div className="flex flex-col items-center space-y-6">
          <div className="flex w-full flex-col items-center gap-3 sm:flex-row sm:justify-center sm:gap-4">
            <ActionButton
              href="https://codeberg.org/discorch/discorch"
              icon={<GitBranch className="h-4 w-4" />}
            >
              Codeberg Repository
            </ActionButton>
          </div>
          <div className="space-y-2 text-center">
            <p className="text-muted-foreground text-sm">
              Open source software built with ❤️
            </p>
          </div>
          <div className="w-full border-t pt-4">
            <p className="text-muted-foreground text-center text-xs">
              This application runs locally on your system — we don't collect or
              store any user data.
            </p>
            <p className="text-muted-foreground mt-2 text-center text-xs">
              Discorch is an independent project and is not affiliated with,
              operated by, or endorsed by Discord Inc. This software is provided
              "as-is" without any warranties, express or implied, including but
              not limited to the implied warranties of merchantability and
              fitness for a particular purpose. Use at your own risk.
            </p>
            <p className="text-muted-foreground mt-2 text-center text-xs">
              Licensed under the{" "}
              <a
                href="https://www.gnu.org/licenses/agpl-3.0.html"
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                AGPLv3
              </a>
              .
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
